import { ASK_HOURS, type AskResponse } from '@bartizo/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { askAi, getAiStatus } from '../api/ai.js';
import { listServers } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { PageHeader } from '../components/PageHeader.js';
import { SeverityBadge } from '../components/StatusBadge.js';
import { effectiveTimezone, formatInstant } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';

/**
 * Asking about the fleet's events (section 13: "what happened on prod-web-01 last
 * night?"; P9.3). The window and the server are picked here, not guessed from the
 * question; the answer cites the events it rests on, each a link.
 */

const WINDOW_LABELS: Record<(typeof ASK_HOURS)[number], string> = {
  1: 'The last hour',
  6: 'The last 6 hours',
  24: 'The last 24 hours',
  72: 'The last 3 days',
  168: 'The last week',
};

const MASK_TEXT: Record<AskResponse['redacted'][number], string> = {
  ips: 'addresses',
  usernames: 'user names',
  internal_hostnames: 'server names',
  paths: 'paths',
};

export function AskPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const status = useQuery({ queryKey: ['ai-status'], queryFn: getAiStatus });
  const servers = useQuery({ queryKey: ['servers'], queryFn: () => listServers({ limit: 200 }) });
  const [question, setQuestion] = useState('');
  const [serverId, setServerId] = useState('');
  const [hours, setHours] = useState<(typeof ASK_HOURS)[number]>(24);

  const ask = useMutation({
    mutationFn: () =>
      askAi({
        question: question.trim(),
        hours,
        ...(serverId === '' ? {} : { serverId }),
      }),
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    ask.mutate();
  };

  const available = status.data?.tasks.ask === true;

  return (
    <div className="page">
      <PageHeader
        title="Ask"
        description="A question about your servers’ events, answered by a language model from the events themselves. It can be wrong: the events it cites are linked, so check them."
      />
      {status.data !== undefined && !available && (
        <Alert tone="info">
          No model is set up to answer questions. An admin can route one on the AI page.
        </Alert>
      )}
      <Card title="Your question">
        <form className="stack" onSubmit={submit}>
          <div className="field">
            <label htmlFor="ask-question">Question</label>
            <textarea
              id="ask-question"
              rows={3}
              maxLength={500}
              required
              placeholder="What happened on web-01 last night?"
              value={question}
              onChange={(event) => {
                setQuestion(event.target.value.replace(/\n/g, ' '));
              }}
            />
          </div>
          <div className="filters">
            <SelectField
              label="Server"
              value={serverId}
              onChange={(event) => {
                setServerId(event.target.value);
              }}
            >
              <option value="">Every server</option>
              {(servers.data?.items ?? []).map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Window"
              value={String(hours)}
              onChange={(event) => {
                const picked = ASK_HOURS.find((each) => String(each) === event.target.value);
                if (picked !== undefined) setHours(picked);
              }}
            >
              {ASK_HOURS.map((each) => (
                <option key={each} value={String(each)}>
                  {WINDOW_LABELS[each]}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="row">
            <Button
              type="submit"
              variant="primary"
              disabled={!available || question.trim().length < 3}
              busy={ask.isPending}
              busyLabel="Asking…"
            >
              Ask
            </Button>
          </div>
        </form>
      </Card>
      {ask.isError && (
        <Alert tone="danger">
          {ask.error instanceof Error ? ask.error.message : 'The model did not answer.'}
        </Alert>
      )}
      {ask.data !== undefined && (
        <Card
          title="Answer"
          footer={
            <span className="cell-sub">
              {ask.data.providerName} · {ask.data.model} · from {ask.data.eventsShown} of{' '}
              {ask.data.eventsInWindow} events in the window
              {ask.data.redacted.length > 0 &&
                ` · sent with ${ask.data.redacted.map((each) => MASK_TEXT[each]).join(', ')} masked`}
            </span>
          }
        >
          <div className="stack">
            {ask.data.answer.split(/\n{2,}/).map((paragraph, index) => (
              <p key={`${String(index)}:${paragraph.slice(0, 20)}`}>{paragraph}</p>
            ))}
            {ask.data.cited.length > 0 && (
              <>
                <strong>Events it rests on</strong>
                <ul>
                  {ask.data.cited.map((event) => (
                    <li key={event.id}>
                      <SeverityBadge severity={event.severity} />{' '}
                      <Link to={`/events/${encodeURIComponent(event.id)}`}>{event.message}</Link>
                      <span className="cell-sub">
                        {event.serverName} · {formatInstant(event.occurredAt, { timezone })}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
